import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import DateRange, {
  StandardAndCustomRangeType,
} from "@mapmycustomers/shared/types/range/DateRange";

import UserDrillDown from "@app/scene/dashboard/components/Sidebar/components/UserDrillDown";
import SortOrder from "@app/scene/dashboard/components/Sidebar/enum/SortOrder";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import {
  getPerformanceData,
  getStackRankViewState,
  isPerformanceDataLoading,
} from "@app/scene/dashboard/store/selectors";
import {
  fetchMetrics,
  updateStackRankViewState,
} from "@app/scene/dashboard/store/stackRank/actions";
import StackRankViewState from "@app/scene/dashboard/store/stackRank/StackRankViewState";
import PerformanceData from "@app/scene/dashboard/types/PerformanceData";
import PerformanceFilters from "@app/scene/dashboard/types/PerformanceFilters";
import { isStackRankAvailable } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import Scope from "@app/types/dashboard/Scope";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useLowerCaseState from "@app/util/hook/useLowerCaseState";

import CollapseExpandButton from "./CollapseExpandButton";
import ScopeList from "./ScopeList";
import Subtitle from "./Subtitle";
import Title from "./Title";
import UserSearchField from "./UserSearchField";
import ViewControls from "./ViewControls";

interface OwnProps {
  activeScope: Scope;
  fixed?: boolean;
  onShowDrillDown?: () => void;
  onToggle?: (collapsed: boolean) => void;
  view: ViewType;
}

interface Props extends OwnProps {
  onFetchMetrics: (payload: Partial<StackRankViewState>) => void;
  onUpdateStackRankViewState: (payload: Partial<StackRankViewState>) => void;
  performanceData: PerformanceData;
  performanceDataLoading?: boolean;
  stackRankVisibility: boolean;
  viewState: StackRankViewState;
}

const SidebarContent: FC<Props> = ({
  activeScope,
  fixed,
  onFetchMetrics,
  onShowDrillDown,
  onToggle,
  onUpdateStackRankViewState,
  performanceData,
  performanceDataLoading,
  stackRankVisibility,
  view,
  viewState,
}) => {
  const analytics = useAnalytics();
  const [query, setQuery] = useLowerCaseState("");

  const { dateRange, dateRangeType, filters, rankingMetric, sort } = viewState;

  const handleDateRangeChange = useCallback(
    (payload: { dateRange: DateRange; dateRangeType: StandardAndCustomRangeType }) => {
      analytics.clicked(["Change date range", "apply"]);
      onFetchMetrics(payload);
    },
    [onFetchMetrics, analytics]
  );

  const handleFiltersChange = useCallback(
    (filters: PerformanceFilters) => onFetchMetrics({ filters }),
    [onFetchMetrics]
  );

  const handleRankingMetricChange = useCallback(
    (rankingMetric: IField) => {
      analytics.clicked(["Change ranking metric"]);
      onFetchMetrics({ rankingMetric });
    },
    [onFetchMetrics, analytics]
  );

  const handleSortChange = useCallback(
    (sort: SortOrder) => {
      analytics.clicked(["Change sort", sort === SortOrder.RANK ? "numeric" : "alphabetical"]);
      onUpdateStackRankViewState({ sort });
    },
    [onUpdateStackRankViewState, analytics]
  );

  const handleShowDrillDown = useCallback(() => {
    analytics.clicked(["Full screen"]);
    onShowDrillDown?.();
  }, [analytics, onShowDrillDown]);

  return (
    <>
      <CollapseExpandButton fixed={fixed} onToggle={onToggle} />
      <Title scope={activeScope} view={view} />
      {stackRankVisibility && (
        <Subtitle
          dateRange={dateRange}
          dateRangeType={dateRangeType}
          onChange={handleDateRangeChange}
          onShowDrillDown={handleShowDrillDown}
        />
      )}
      <UserSearchField onChange={setQuery} value={query} />
      {stackRankVisibility && (
        <ViewControls
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onRankingMetricChange={handleRankingMetricChange}
          onSortChange={handleSortChange}
          rankingMetric={rankingMetric}
          sort={sort}
        />
      )}
      <ScopeList
        activeScope={activeScope}
        performanceData={performanceData}
        performanceDataLoading={performanceDataLoading}
        query={query}
        rankingMetric={rankingMetric}
        sort={sort}
        view={view}
      />
      <UserDrillDown dateRange={dateRange} rankingMetric={rankingMetric} />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  performanceData: getPerformanceData(state),
  performanceDataLoading: isPerformanceDataLoading(state),
  stackRankVisibility: isStackRankAvailable(state),
  viewState: getStackRankViewState(state),
});

const mapDispatchToProps = {
  onFetchMetrics: fetchMetrics.request,
  onUpdateStackRankViewState: updateStackRankViewState,
};

export default connect(mapStateToProps, mapDispatchToProps)(SidebarContent);
